import {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {MIN_MAX_OUTPUT, OUTPUT_DATA} from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import { AdvanceSearchByFilters, CreateHidOutput, GetAllSiosByHidId} from "../../../../../reduxToolkit/HID/HidApi";
import {toast} from "react-toastify";
import {errorMsg, isNumber} from "../../../../../Apis/functions";
import {useDispatch, useSelector} from "react-redux";
import {TABLES} from "../../../../../Apis/Tables";
import { SearchByFilters } from '../../../../../reduxToolkit/Search/SearchApi';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Custom React hook that manages the creation of HID outputs.
 * Handles form validation, API calls, and UI feedback (toasts and form resets).
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onHide - Callback to close the modal or dialog after creation
 * @param {string|null} [props.controllerId=null] - Optional controller ID associated with the output
 * @param {Function} props.setModified - Callback to flag the parent component when data has changed
 *
 * @returns {Object} An object exposing state variables and handler functions for the output creation process
 */
const CreateOutputApp = ({ onHide, controllerId = null, setModified }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    /** Redux selectors */
     const { advanceSearchByFilters } = useSelector((state) => state.HidSlice)
    const sioIds = useSelector((state) => state.HidSlice.getAllSiosByHidId);

    /** Local state for form and validation */
    const [output, setOutput] = useState({ ...OUTPUT_DATA, controllerId });
    const { handleChange } = HandleChange({ model: output, setModel: setOutput });
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidDefaultPulse, setIsValidDefaultPulse] = useState(true);

    /**
     * Loads all HID controllers if no controllerId was passed.
     * This is used for populating dropdowns or selection lists.
     */
    useEffect(() => {
        if (!controllerId) {
            dispatch(AdvanceSearchByFilters({ option: TABLES.HID, body: {} }));
        }
    }, [controllerId]);

    /**
     * Loads all SIOs (Serial Input/Output devices) when the controller ID changes.
     */
    useEffect(() => {
        if (output.controllerId) {
            dispatch(GetAllSiosByHidId({ hidId: output.controllerId })).catch(() => {});
        }
    }, [output.controllerId]);

    /**
     * Validates the `defaultPulse` value whenever it changes.
     * Ensures the value is numeric and within the defined range.
     */
    useEffect(() => {
        if (output.defaultPulse !== null && output.defaultPulse !== "") {
            const defaultPulse = parseInt(output.defaultPulse);
            if (isNumber(defaultPulse) && defaultPulse > MIN_MAX_OUTPUT.DEFAULT_PULSE.start && defaultPulse <= MIN_MAX_OUTPUT.DEFAULT_PULSE.end) {
                setIsValidDefaultPulse(true);
                return;
            }
            setIsValidDefaultPulse(false);
        }
    }, [output.defaultPulse]);

    /**
     * Handles the submission of the HID Output creation form.
     * Validates the model and sends an API request via Redux to create the new record.
     *
     * @function
     */
    const handleSubmit = () => {
        if (!output.isValid() || !isValidDefaultPulse) {
            if (!output.isValid()) setEmptyFields(true);
            return;
        }

        const body = output.toAddEdit();

        dispatch(CreateHidOutput(body)).then(({ payload: { data: { success, code } } }) => {
            if (success === true) {
                toast.success(t('has_been_created_successfully'));
                resetValues();
                setModified(true);
                onHide();
            } else {
                errorMsg(code);
            }
        });
    };

    /**
     * Resets all form fields to their default values.
     *
     * @function
     */
    const resetValues = () => {
        setOutput({ ...OUTPUT_DATA });
    };

    return {
        t,
        output,
        handleChange,
        emptyFields,
        isValidDefaultPulse,
        controllers: advanceSearchByFilters,
        sioIds,
        handleSubmit,
        resetValues
    };
};

export default CreateOutputApp;
